import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import type { SitecoreNavigation } from '@/sitecoreState';

export interface JumpMark {
  offsetTop: number;
  [key: string]: any;
}

export interface BackLink {
  text: string;
  url: string;
  clickEvent?: boolean;
}

export const useNavigationStore = defineStore('useNavigationStore', () => {
  const showFlyout = ref(false);
  const showMobileFirstLvl = ref(false);
  const showMobileSubLvl = ref(false);
  const showSearchSuggest = ref(false);
  const enableHandlingFeeBanner = ref<boolean | 'init'>('init');
  const requestHandlingFeeBanner = ref(false);
  const isScrolledOutHandlingFeeBanner = ref(false);
  const isAssistedServiceActive = ref(false);
  const isAssistedServiceSalesforce = ref(false);
  const assistedServiceQuoteHeight = ref(0);
  const navIndex = ref(-1);
  const groupData = ref<SitecoreNavigation | undefined>();
  const subElements = ref<any[]>([]);
  const isProducts = ref(false);
  const isCompany = ref(false);
  const isCountrySelect = ref(false);
  const isCountrySelectPage = ref(false);
  const isSingleLocale = ref(true);
  const isCheckout = ref(true);
  const brandAreaIndex = ref(0);
  const _showBrandArea = ref(true);
  const searchInput = ref(false);
  const searchFocused = ref(false);
  const searchTransitioning = ref(false);
  const isSearchPage = ref(false);
  const backLink = ref<BackLink | null>(null);
  const backLinkClicked = ref(false);
  const currentLocaleData = ref<any>(null);
  const jumpMarks = ref<JumpMark[]>([]);
  const activeJumpMark = ref<Record<string, any>>({});
  const productBreadcrumb = ref<any[]>([]);

  // Computed getters
  const showBrandArea = computed(() => _showBrandArea.value && !showSearchSuggest.value);
  /** Also decides the height every .header-height element reserves for the banner. */
  const showHandlingFeeBanner = computed(
    () =>
      enableHandlingFeeBanner.value &&
      requestHandlingFeeBanner.value &&
      !isScrolledOutHandlingFeeBanner.value,
  );
  const searchActive = computed(
    () => searchInput.value || showSearchSuggest.value || isSearchPage.value,
  );
  const backLinkData = computed(() => backLink.value || { text: '', url: '', clickEvent: false });

  // Actions
  const setShowFlyout = (value: boolean) => {
    showFlyout.value = value;
  };

  const setShowMobileFirstLvl = (value: boolean) => {
    showMobileFirstLvl.value = value;
  };

  const setShowMobileSubLvl = (value: boolean) => {
    showMobileSubLvl.value = value;
  };

  const setShowSearchSuggest = (value: boolean) => {
    showSearchSuggest.value = Boolean(value);
    if (value) {
      navIndex.value = -1;
    }
  };

  const setEnableHandlingFeeBanner = (value: boolean) => {
    enableHandlingFeeBanner.value = Boolean(value);
  };

  const setRequestHandlingFeeBanner = (value: boolean) => {
    requestHandlingFeeBanner.value = Boolean(value);
  };

  const setIsScrolledOutHandlingFeeBanner = (value: boolean) => {
    isScrolledOutHandlingFeeBanner.value = Boolean(value);
  };

  const setNavIndex = (value: number) => {
    navIndex.value = value;
    if (value !== -1) {
      showSearchSuggest.value = false;
    }
  };

  const setGroupData = (value: SitecoreNavigation | undefined) => {
    groupData.value = value;
  };

  const setSubElements = (value: any[]) => {
    subElements.value = value;
  };

  const setIsProducts = (value: boolean) => {
    isProducts.value = Boolean(value);
  };

  const setIsCompany = (value: boolean) => {
    isCompany.value = Boolean(value);
  };

  const setIsCountrySelect = (value: boolean) => {
    isCountrySelect.value = Boolean(value);
  };

  const setIsCountrySelectPage = (value: boolean) => {
    isCountrySelectPage.value = Boolean(value);
  };

  const setIsSingleLocale = (value: boolean) => {
    isSingleLocale.value = Boolean(value);
  };

  const setIsCheckout = (value: boolean) => {
    isCheckout.value = Boolean(value);
  };

  const setBrandAreaIndex = (value: number) => {
    brandAreaIndex.value = value;
  };

  const setShowBrandArea = (value: boolean) => {
    _showBrandArea.value = value;
  };

  const setSearchInput = (value: boolean) => {
    searchInput.value = value;
  };

  const setSearchFocused = (value: boolean) => {
    searchFocused.value = value;
  };

  const setSearchTransitioning = (value: boolean) => {
    searchTransitioning.value = value;
  };

  const setIsSearchPage = (value: boolean) => {
    isSearchPage.value = value;
  };

  const setBackLink = (value: BackLink | null) => {
    backLink.value = value;
  };

  const setBackLinkClicked = (value: boolean) => {
    backLinkClicked.value = value;
  };

  const setAssistedServiceActive = (value: boolean) => {
    isAssistedServiceActive.value = value;
  };

  const setAssistedServiceSalesforce = (value: boolean) => {
    isAssistedServiceSalesforce.value = value;
  };

  /**
   * The rendered height of the salesforce quote block, measured by the assisted service module.
   * The block grows and shrinks with the session data it shows, so consumers of the header height
   * cannot rely on a fixed value for it.
   */
  const setAssistedServiceQuoteHeight = (value: number) => {
    assistedServiceQuoteHeight.value = value;
  };

  const setCurrentLocaleData = (value: any) => {
    currentLocaleData.value = value;
  };

  const setJumpMark = (jumpMark: JumpMark) => {
    jumpMarks.value.push(jumpMark);
    jumpMarks.value.sort((a, b) => a.offsetTop - b.offsetTop);
  };

  const setActiveJumpMark = (value: Record<string, any>) => {
    activeJumpMark.value = value;
  };

  const setProductBreadcrumb = (value: any[]) => {
    productBreadcrumb.value = value;
  };

  return {
    showFlyout,
    showMobileFirstLvl,
    showMobileSubLvl,
    showSearchSuggest,
    enableHandlingFeeBanner,
    requestHandlingFeeBanner,
    isScrolledOutHandlingFeeBanner,
    showHandlingFeeBanner,
    isAssistedServiceActive,
    isAssistedServiceSalesforce,
    assistedServiceQuoteHeight,
    navIndex,
    groupData,
    subElements,
    isProducts,
    isCompany,
    isCountrySelect,
    isCountrySelectPage,
    isSingleLocale,
    isCheckout,
    brandAreaIndex,
    showBrandArea,
    searchInput,
    searchFocused,
    searchTransitioning,
    isSearchPage,
    backLink,
    backLinkClicked,
    currentLocaleData,
    jumpMarks,
    activeJumpMark,
    productBreadcrumb,
    searchActive,
    backLinkData,
    setShowFlyout,
    setShowMobileFirstLvl,
    setShowMobileSubLvl,
    setShowSearchSuggest,
    setEnableHandlingFeeBanner,
    setRequestHandlingFeeBanner,
    setIsScrolledOutHandlingFeeBanner,
    setNavIndex,
    setGroupData,
    setSubElements,
    setIsProducts,
    setIsCompany,
    setIsCountrySelect,
    setIsCountrySelectPage,
    setIsSingleLocale,
    setIsCheckout,
    setBrandAreaIndex,
    setShowBrandArea,
    setSearchInput,
    setSearchFocused,
    setSearchTransitioning,
    setIsSearchPage,
    setBackLink,
    setBackLinkClicked,
    setAssistedServiceActive,
    setAssistedServiceSalesforce,
    setAssistedServiceQuoteHeight,
    setCurrentLocaleData,
    setJumpMark,
    setActiveJumpMark,
    setProductBreadcrumb,
  };
});
